import {
  ArrowPathIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  useCallback,
  useEffect,
  Button,
} from "@";

// Below `md` the default chrome becomes a full-screen filter sheet.
const MOBILE_QUERY = "(max-width: 767px)";

const isMobileViewport = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(MOBILE_QUERY).matches;

const MultiSearchForm = ({
  show,
  style,
  reset,
  onClose,
  onSubmit,
  className,
  children,
}) => {
  // When `style` overrides, skip the header/footer chrome — and the mobile sheet.
  const isCustomStyled = !!style;

  const requestClose = useCallback(() => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }
    // Fallback: flip the page-level filter toggle (the button that opened us).
    if (typeof document !== "undefined") {
      document.getElementById("search-toggle")?.click();
    }
  }, [onClose]);

  // Auto-close after submit / reset, but only while we're the full-screen sheet.
  const closeIfMobile = useCallback(() => {
    if (isMobileViewport()) requestClose();
  }, [requestClose]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      onSubmit();
      closeIfMobile();
    },
    [onSubmit, closeIfMobile],
  );

  const handleReset = useCallback(() => {
    reset?.();
    closeIfMobile();
  }, [reset, closeIfMobile]);

  // Lock body scroll + wire Escape while the mobile sheet is open.
  useEffect(() => {
    if (isCustomStyled || !show || !isMobileViewport()) return;

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isCustomStyled, show, requestClose]);

  if (!show) return null;

  // Default refined card; callers can fully override with the `style` prop.
  const wrapperClass = isCustomStyled
    ? style
    : [
        // inline card (md and up)
        "relative my-4 md:my-6 rounded-2xl border border-gray-100 bg-white shadow-sm shadow-gray-900/[0.02] dark:border-gray-600/40 dark:bg-gray-800",
        // full-screen sheet (below md)
        "max-md:fixed max-md:inset-0 max-md:z-[60] max-md:my-0 max-md:flex max-md:flex-col max-md:rounded-none max-md:border-0 max-md:bg-white max-md:shadow-none max-md:animate-[fadeIn_0.25s_ease-out] dark:max-md:bg-gray-900",
      ].join(" ");

  return (
    <div className={`multi-search ${wrapperClass}`}>
      <form
        onSubmit={handleSubmit}
        className={
          isCustomStyled
            ? "relative"
            : "relative max-md:flex max-md:min-h-0 max-md:flex-1 max-md:flex-col"
        }
      >
        {/* Header — only on default chrome */}
        {!isCustomStyled && (
          <header className="relative flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gradient-to-l from-purple-50/40 via-white/40 to-transparent px-4 py-3 dark:border-gray-700 dark:from-purple-950/20 dark:via-gray-700 max-md:shrink-0 max-md:flex-nowrap md:px-5">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-purple-50 text-purple-700 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
                <FunnelIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-purple-700 dark:text-purple-400">
                  التصفية
                </p>
                <h3 className="text-sm font-bold tracking-tight text-gray-900 dark:text-gray-100">
                  تصفية النتائج
                </h3>
              </div>
            </div>

            {/* Hint — desktop only */}
            <p className="hidden text-xs text-gray-500 dark:text-gray-400 md:inline-flex md:items-center md:gap-1.5">
              <kbd className="inline-flex items-center rounded-md border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
                Enter
              </kbd>
              للبحث
            </p>

            {/* Close — full-screen sheet only */}
            <button
              type="button"
              onClick={requestClose}
              aria-label="إغلاق التصفية"
              title="إغلاق التصفية"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-gray-100 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 md:hidden"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </header>
        )}

        {/* Inputs grid */}
        <div
          className={`bg-gray-50 md:bg-white dark:bg-gray-800 ${
            isCustomStyled
              ? ""
              : "px-4 py-5 max-md:min-h-0 max-md:flex-1 max-md:overflow-y-auto md:px-5"
          }`}
        >
          <div className={className}>{children}</div>
        </div>

        {/* Footer toolbar */}
        <footer
          className={
            isCustomStyled
              ? "mt-4 flex flex-wrap items-center justify-end gap-2"
              : "flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/50 px-4 py-3 dark:border-gray-700/60 dark:bg-gray-900/40 max-md:shrink-0 md:px-5"
          }
        >
          <button className="px-4 py-2 btn-purple-900 flex items-center gap-1" type="submit">
            <MagnifyingGlassIcon className="h-4 w-4" />
            <span className="relative">بحث</span>
          </button>
          {reset && (
            <button
              onClick={handleReset}
              type="button"
              className="group inline-flex items-center gap-2 rounded-full border border-gray-100 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 transition-all duration-200 hover:-translate-y-px hover:border-purple-200 hover:bg-purple-50 hover:text-purple-900 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-purple-700 dark:hover:bg-gray-700 dark:hover:text-purple-300"
            >
              <ArrowPathIcon className="h-4 w-4 transition-transform duration-500 group-hover:-rotate-180" />
              إعادة تعيين
            </button>
          )}
        </footer>
      </form>
    </div>
  );
};

export default MultiSearchForm;
